import { api, NotifyModelKey } from "./api";
import { Faculty, Post } from "./posts";
import { trackUnsavedInput, normalizeForSearch, attachDropdownKeyboardNav, clampDropdownToViewport, splitFullName, navigateSmoothly } from "./generics";
import { renderPostHocNotifyPrompt } from "./notifyEmail";
import { initCommentForm, makeCollapsible, Comment } from "./comments";

// Wydział-zbiornik z importu ogólnopolskiej bazy naukowców (patrz PLACEHOLDER_FACULTY_SLUG w
// import_naukowcy.py) - czysto techniczny fallback, nigdy nie pokazujemy go jako wybieralną
// opcję w formularzu (patrz renderAddPersonForm niżej), żeby nie mylić użytkownika.
const PLACEHOLDER_FACULTY_SLUG = "bez-wydzialu";

// Tylko jeden naraz - drugi klik (np. w inną kartę, zanim pierwszy dialog się zamknął) zamyka
// poprzedni zamiast układać je jeden na drugim (natywny <dialog>/top layer by na to pozwolił) -
// ten sam wzorzec co dawniej w quickReview.ts.
let openDialog: HTMLDialogElement | null = null;

// Stały, wyraźny przycisk obok pola wyszukiwania - listy wyszukiwania (browser.ts/
// post_detail.ts) pokazują tylko osoby już faktycznie przypisane do wydziału (patrz
// PostListView.get_queryset w pages/api/posts.py) - kogokolwiek nowego, albo kogoś wciąż w
// koszyku "Bez przypisanego wydziału", da się dodać wyłącznie stąd. Ten sam dialog otwiera też
// mniejszy przycisk pod wynikami wyszukiwania (patrz small niżej i renderAddPersonPrompt w
// browser.ts) - to tylko inny punkt wejścia do identycznego formularza.
// getPrefillQuery - wołane dopiero w momencie kliknięcia (nie przy renderowaniu przycisku), żeby
// podchwycić to, co user WŁAŚNIE ma wpisane w polu wyszukiwania (patrz splitFullName niżej).
// small - wariant "Dodaj" (bez "osobę") pod wynikami wyszukiwania (patrz .add-lecturer-prompt w
// style.css) - tam kontekst ("Nie znalazłeś wykładowcy?") już mówi, o co chodzi, więc dłuższa
// etykieta byłaby powtórzeniem. Gdy jest queryLabel (to, co user aktualnie wpisał w wyszukiwarkę),
// dokładamy je do etykiety ("Dodaj „anna nowa"") - inaczej niż getPrefillQuery, to wołane RAZ przy
// renderowaniu (ten przycisk i tak jest tworzony od nowa przy każdym render() listy, patrz
// browser.ts), więc nie trzeba tego opóźniać do kliknięcia.
// emphasized - tylko dla wariantu small: ciemny, wypełniony wygląd (patrz .add-comment-btn-
// emphasized w style.css) zamiast zwykłego obrysu - używane, gdy to JEDYNY sposób dodania osoby
// na tej stronie (patrz MANY_POSTS_THRESHOLD w browser.ts - duży przycisk obok wyszukiwarki
// znika przy długiej liście), żeby nie wyglądał jak zwykły, drugorzędny przycisk.
// large - tylko dla wariantu small: powiększona wersja pigułki (patrz .add-comment-btn-large w
// style.css), gdy przefiltrowana lista wyników jest na tyle krótka, że to i tak jedyna sensowna
// akcja na widoku (patrz resultsFitOnScreen w browser.ts) - sam rozmiar/waga przycisku, etykieta
// taka sama jak zwykły mały wariant ("Dodaj „X”").
export function renderAddPersonButton(
    container: HTMLElement,
    universitySlug: string | null,
    facultySlug: string | null,
    getPrefillQuery: () => string,
    small = false,
    emphasized = false,
    queryLabel?: string,
    large = false
): void {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = small ? "add-comment-btn" : "add-person-btn";
    if (small && emphasized) btn.classList.add("add-comment-btn-emphasized");
    if (small && large) btn.classList.add("add-comment-btn-large");
    const label = small && queryLabel
        ? `Dodaj „${queryLabel}”`
        : small ? "Dodaj" : "Dodaj osobę";
    const icon = document.createElement("span");
    icon.className = "add-comment-btn-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "+";
    btn.append(icon, ` ${label}`);
    btn.addEventListener("click", () => {
        openAddPersonDialog(universitySlug, facultySlug, getPrefillQuery());
    });
    container.appendChild(btn);
}

// Eksportowane osobno od renderAddPersonButton - post_detail.ts otwiera ten sam dialog wprost z
// własnej podpowiedzi wyszukiwania (patrz tam), zamiast przez gotowy przycisk-pigułkę, żeby
// pasował stylem do reszty tej rozwijanej listy (search-suggestion-item, nie add-comment-btn).
export function openAddPersonDialog(
    universitySlug: string | null,
    facultySlug: string | null,
    prefillQuery: string
): void {
    openDialog?.close();

    const dialog = document.createElement("dialog");
    // .add-review-dialog - ta sama klasa co dawniej quickReview.ts, żeby popup wyglądał
    // identycznie jak znany już "Dodaj pierwszą opinię". .add-person-dialog dokłada tylko
    // overflow:visible (patrz style.css) - dopóki podpowiedzi (position:absolute) mogą się
    // otworzyć, dialog NIE ma przycinać/scrollować, żeby lista mogła wystawać poza jego obrys
    // (patrz clampDropdownToViewport w generics.ts - to ONA pilnuje, żeby nie wystawała też poza
    // sam ekran). Po rozstrzygnięciu, kim jest dodawana osoba (patrz restoreDialogScroll niżej),
    // podpowiedzi już się nie otworzą ponownie - dialog wraca wtedy do zwykłego overflow-y:auto,
    // żeby dało się przescrollować do przycisku wysyłki, gdy cały formularz opinii jest wyższy niż
    // ekran (zgłoszenie: "Podziel się opinią" ucięte na dole, niedostępne).
    dialog.className = "add-review-dialog add-person-dialog";

    // Nad tytułem (nie w treści formularza, patrz renderAddPersonForm) - kontekst "gdzie" jest
    // stały przez cały czas życia tego dialogu, więc czytelniej jako osobny pasek nad główną
    // treścią niż jako kolejne pole formularza. Pusty na starcie - wypełnia go dopiero
    // renderAddPersonForm, gdy wie, czy wydział jest znany z kontekstu strony (patrz
    // facultyContextEl niżej).
    const facultyContextEl = document.createElement("p");
    facultyContextEl.className = "add-person-faculty-note";

    const dialogTitle = document.createElement("h2");
    dialogTitle.className = "add-review-dialog-title";
    dialogTitle.textContent = "Dodaj osobę";

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "add-review-dialog-close";
    closeBtn.setAttribute("aria-label", "Zamknij");
    closeBtn.innerHTML = "&times;";
    closeBtn.addEventListener("click", () => dialog.close());

    const formContainer = document.createElement("div");
    formContainer.className = "comment-form-box";

    dialog.append(facultyContextEl, dialogTitle, closeBtn, formContainer);
    document.body.appendChild(dialog);

    // Klik w tło (::backdrop) dociera do JS jako klik na sam element <dialog> - ten sam wzorzec
    // co w quickReview.ts/post_detail.ts.
    dialog.addEventListener("click", (event) => {
        if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener("close", () => {
        if (openDialog === dialog) openDialog = null;
        dialog.remove();
    });
    openDialog = dialog;

    dialog.showModal();

    renderAddPersonForm(
        formContainer, universitySlug, facultySlug, prefillQuery,
        () => dialog.close(),
        () => {
            // dialog.remove() WPROST (nie dialog.close()) - patrz removeDialogBeforeNavigate w
            // renderAddPersonForm, gdzie to jest jedynym powodem tego callbacku.
            if (openDialog === dialog) openDialog = null;
            dialog.remove();
        },
        () => { dialog.style.overflow = "auto"; },
        facultyContextEl,
        (personName) => { dialogTitle.textContent = `Dodaj osobę - ${personName}`; },
    );
}

async function renderAddPersonForm(
    container: HTMLElement,
    universitySlug: string | null,
    facultySlug: string | null,
    prefillQuery: string,
    closeDialog: () => void,
    removeDialogBeforeNavigate: () => void,
    restoreDialogScroll: () => void,
    facultyContextEl: HTMLElement,
    setDialogTitleSuffix: (personName: string) => void
): Promise<void> {
    const loading = document.createElement("p");
    loading.textContent = "Wczytywanie...";
    container.appendChild(loading);

    // Podpowiedzi (patrz renderSuggestions niżej) budujemy z odpowiedzi o CAŁEJ uczelni (bez
    // faculty) - to jedyne zapytanie, które w ogóle zwraca osoby z technicznego koszyka "Bez
    // przypisanego wydziału" (patrz PostListView.get_queryset w pages/api/posts.py - listy
    // per-wydział ich nie zwracają). Filtrujemy do number_of_comments === 0 (patrz poolPosts
    // niżej) - obejmuje to zarówno tę pulę, jak i osoby z już REALNIE przypisanym wydziałem, ale
    // bez żadnego komentarza (np. hurtowy import, patrz zgłoszenie: "Piotr Kossacki"/"Wojciech
    // Satuła") - ci drudzy są już widoczni wprost na liście wydziału, ale warto ich też podpowiadać
    // tutaj, żeby nie trzeba było zamykać popupu i szukać ich osobno.
    const [facultiesResult, poolResult] = await Promise.all([
        api.getFaculties(universitySlug ?? undefined),
        universitySlug ? api.getPosts(universitySlug) : Promise.resolve({ ok: false as const, error: "" }),
    ]);

    container.replaceChildren();

    const errorEl = document.createElement("span");
    errorEl.className = "error";

    let realFaculties: Faculty[] = [];
    let placeholderFacultyId: number | undefined;
    let noFaculties = false;
    if (facultiesResult.ok) {
        realFaculties = facultiesResult.data.faculties.filter(f => f.slug !== PLACEHOLDER_FACULTY_SLUG);
        placeholderFacultyId = facultiesResult.data.faculties.find(f => f.slug === PLACEHOLDER_FACULTY_SLUG)?.id;
        noFaculties = facultiesResult.data.no_faculties;
    } else {
        errorEl.textContent = `Błąd wczytywania wydziałów: ${facultiesResult.error}`;
    }

    // Jeśli już przeglądamy konkretny wydział, dodawana osoba prawie na pewno do niego należy -
    // ten kontekst pokazujemy nad tytułem (patrz facultyContextEl/openAddPersonDialog), nie jako
    // pole formularza - nie da się go tu zmienić, więc nie ma sensu udawać pola do wypełnienia.
    const currentFaculty = facultySlug
        ? realFaculties.find(f => f.slug === facultySlug)
        : undefined;

    if (currentFaculty) {
        facultyContextEl.textContent = `Dodajesz do wydziału: ${currentFaculty.name}`;
    }

    // Gdy dodajemy na KONKRETNY wydział, podpowiedzi mają obejmować tylko osoby, które faktycznie
    // mogą do niego trafić: wciąż w koszyku "Bez przypisanego wydziału" (needs_faculty_confirmation)
    // albo już przypisane właśnie do TEGO wydziału. Bez tego zawężenia pula (patrz poolResult wyżej)
    // ciągnie osoby z całej uczelni - przy popularnym imieniu podpowiadałoby więc kogoś, kto
    // realnie pracuje na zupełnie innym wydziale (zgłoszenie: szukanie "Marta" na MIMUW). W widoku
    // całej uczelni (currentFaculty brak, wydział wybiera się dopiero niżej) zawężenie nie ma sensu
    // - dowolne dopasowanie i tak skończy pod SWOIM realnym wydziałem (patrz attemptResolve/
    // needs_faculty_confirmation niżej), nie pod przypadkowym. BEZ filtra number_of_comments === 0
    // (w odróżnieniu od dawnej wersji tej zmiennej) - osoba, która ma już opinie, dostaje dokładnie
    // taką samą podpowiedź jak osoba bez nich (klik i tak prowadzi do finalizeResolution, czyli
    // dodania KOLEJNEJ opinii tej samej osobie), więc nie ma powodu jej wykluczać - inaczej dokładne
    // dopasowanie imienia takiej osoby nigdy by się nie pojawiło, a "+ Dodaj nową osobę" cicho
    // tworzyłoby jej duplikat zamiast podpowiedzieć, że już istnieje.
    //
    // Uczelnia już podzielona na wydziały (ponad połowa profili ma realny wydział - np. UW po
    // dopasowaniu do list pracowników z USOSweb): na stronie wydziału NIE podpowiadamy osób z
    // koszyka "Bez przypisanego wydziału" (na prośbę 2026-10-23). Tam to zwykle ludzie spoza
    // wydziałów (centra badawcze, byli pracownicy), a klik przypisałby ich do tego wydziału. Na
    // uczelniach znanych tylko z RADON-u (prawie wszyscy w koszyku) koszyk to po prostu cały skład,
    // więc zostaje. Siatka bezpieczeństwa przed duplikatem (allPosts niżej) dalej obejmuje koszyk.
    const poolPosts: Post[] = poolResult.ok ? poolResult.data.posts : [];
    const unassignedCount = poolPosts.filter(p => p.needs_faculty_confirmation).length;
    const universityIsDivided = poolPosts.length > 0 && unassignedCount / poolPosts.length < 0.5;
    const matchPool: Post[] = poolPosts.filter(p =>
        !currentFaculty || p.faculty === currentFaculty.id || (p.needs_faculty_confirmation && !universityIsDivided));

    // Pełna, nieprzefiltrowana (WSZYSCY, z całej uczelni, niezależnie od wydziału) wersja powyższego
    // - wyłącznie jako ostatnia siatka bezpieczeństwa w attemptResolve niżej, tuż przed
    // api.createPost: matchPool bywa zawężone do jednego wydziału (patrz wyżej), więc duplikat na
    // INNYM wydziale by przez nią nie przeszedł (i nie pojawiłby się w podpowiedziach) - to
    // sprawdzenie ma go złapać mimo to, tuż przed samym utworzeniem.
    const allPosts: Post[] = poolResult.ok ? poolResult.data.posts : [];

    // Wydział wybrany PO tym, jak imię/nazwisko jest już rozstrzygnięte (patrz attemptResolve
    // niżej) - wywołuje ponowną próbę rozstrzygnięcia, żeby nie trzeba było klikać podpowiedzi
    // ponownie tylko dlatego, że przy pierwszym kliknięciu wydział nie był jeszcze wybrany.
    // element poniżej dla currentFaculty nigdy nie trafia do DOM (patrz container.append niżej) -
    // zostaje tylko po to, żeby kształt zwracanego obiektu był identyczny w obu gałęziach.
    const facultyPicker = currentFaculty
        ? {
            element: document.createElement("div"),
            getSelection: () => currentFaculty.id,
            reset: () => {},
        }
        : !noFaculties && realFaculties.length >= 2
            ? renderFacultyPicker(universitySlug, realFaculties, () => attemptResolve())
            : {
                element: document.createElement("div"),
                getSelection: () => realFaculties[0]?.id ?? placeholderFacultyId,
                reset: () => {},
            };

    const nameField = document.createElement("label");
    nameField.className = "lecturer-field";
    const nameLabel = document.createElement("span");
    nameLabel.className = "field-label-text";
    nameLabel.textContent = "Imię i nazwisko:";

    const nameSearchWrapper = document.createElement("div");
    nameSearchWrapper.className = "search-with-suggestions";

    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.required = true;
    nameInput.maxLength = 400;
    nameInput.autocomplete = "off";
    nameInput.value = prefillQuery;
    trackUnsavedInput(nameInput);

    const suggestionsDropdown = document.createElement("div");
    suggestionsDropdown.className = "search-suggestions";

    nameSearchWrapper.append(nameInput, suggestionsDropdown);

    nameField.append(nameLabel, nameSearchWrapper);

    const resolveError = document.createElement("span");
    resolveError.className = "error";

    // Ostrzeżenie o duplikacie (patrz allPosts wyżej i sprawdzenie w attemptResolve niżej) - osobny
    // element od resolveError, bo oprócz tekstu zawiera link do istniejącego profilu, którego samo
    // <span class="error"> nie udźwignie.
    const duplicateNotice = document.createElement("p");
    duplicateNotice.className = "add-person-duplicate-notice";

    // Formularz pierwszego komentarza jest widoczny OD RAZU w layoucie (nie chowany za osobnym
    // przyciskiem "Dalej") - pusty na starcie (collapsible-collapsed, patrz makeCollapsible w
    // comments.ts), płynnie się rozwija (grid-template-rows animowane w CSS), gdy wiadomo, kogo
    // dodajemy (patrz revealCommentArea/mountCommentForm niżej) - bez przeskoku layoutu.
    const commentAreaCollapsible = makeCollapsible();
    commentAreaCollapsible.outer.classList.add("collapsible-collapsed");
    const commentArea = commentAreaCollapsible.inner;

    function revealCommentArea(): void {
        commentAreaCollapsible.outer.classList.remove("collapsible-collapsed");
    }

    // Imię/nazwisko i (gdy trzeba wybrać - patrz facultyPicker wyżej) wydział jeden pod drugim
    // (nie obok siebie w jednej linii) - czytelniej przy dłuższych nazwach wydziałów, patrz
    // .comment-form-box label wyżej w style.css (każdy to osobny <label>, więc domyślnie i tak
    // się układają w osobnych wierszach). Gdy wydział jest już znany z kontekstu strony
    // (currentFaculty), facultyPicker.element nigdy tu nie trafia - ten kontekst pokazuje się nad
    // tytułem (patrz facultyContextEl).
    const facultyFields = currentFaculty ? [] : [facultyPicker.element];
    container.append(nameField, ...facultyFields, errorEl, resolveError, duplicateNotice, commentAreaCollapsible.outer);

    let selectedPost: Post | undefined;
    // Ustawiane raz, gdy uda się rozstrzygnąć, kim jest ta osoba (patrz attemptResolve niżej) -
    // blokuje ponowne rozstrzygnięcie (i przypadkowe podwójne api.createPost) przy kolejnych
    // kliknięciach/zmianach wydziału.
    let resolved: { postId: number, facultyId: number, personCreatedNow: boolean } | undefined;

    function closeSuggestions(): void {
        suggestionsDropdown.classList.remove("open");
    }

    function matchesQuery(post: Post, normalizedQuery: string): boolean {
        const name = normalizeForSearch(post.name);
        const surname = normalizeForSearch(post.surname);
        return name.includes(normalizedQuery) || surname.includes(normalizedQuery)
            || `${name} ${surname}`.includes(normalizedQuery) || `${surname} ${name}`.includes(normalizedQuery);
    }

    function renderSuggestions(query: string): void {
        suggestionsDropdown.replaceChildren();
        const normalizedQuery = normalizeForSearch(query);
        if (!normalizedQuery) {
            closeSuggestions();
            return;
        }

        const matches = matchPool.filter(post => matchesQuery(post, normalizedQuery));

        // Zawsze NA GÓRZE listy (nie na dole pod podpowiedziami z bazy) - to najczęstszy wybór
        // (osoby, której szukasz, zwykle jeszcze nie ma w systemie), więc nie ma czekać, aż
        // ktoś przewinie wzrokiem całą listę dopasowań, żeby ją w ogóle zauważyć. Zawsze widoczna
        // (chyba że to dokładnie jedna z podpowiedzi niżej), żeby było jasne, że wcale nie trzeba
        // wybierać z listy - wpisanie własnej odpowiedzi jest równorzędną opcją (analogicznie do
        // "+ Dodaj wydział" w renderFacultyPicker niżej).
        const trimmedQuery = query.trim();
        const exactMatch = matchPool.some(post => normalizeForSearch(`${post.name} ${post.surname}`) === normalizedQuery);
        if (trimmedQuery && !exactMatch) {
            const ownOption = document.createElement("button");
            ownOption.type = "button";
            ownOption.className = "search-suggestion-item search-suggestion-create";
            ownOption.textContent = `+ Dodaj nową osobę: "${trimmedQuery}"`;
            ownOption.addEventListener("click", () => {
                selectedPost = undefined;
                attemptResolve();
            });
            suggestionsDropdown.appendChild(ownOption);
        }

        if (matches.length > 0) {
            const header = document.createElement("div");
            header.className = "search-suggestions-header";
            header.textContent = "Czy chodziło Ci o:";
            suggestionsDropdown.appendChild(header);
        }

        matches.forEach(post => {
            const item = document.createElement("button");
            item.type = "button";
            item.className = "search-suggestion-item";
            item.append(`${post.name} ${post.surname}`);
            // Odróżnia od osób bez żadnej opinii jeszcze (matchPool ich nie rozróżnia wizualnie
            // inaczej) - klik i tak prowadzi do tego samego finalizeResolution/mountCommentForm
            // (dodanie KOLEJNEJ opinii tej samej osobie), ale ten dopisek od razu mówi, że to nie
            // "pierwsza opinia", tylko istniejący, już oceniany profil.
            if (post.number_of_comments > 0) {
                const hint = document.createElement("span");
                hint.className = "search-suggestion-hint";
                hint.textContent = ` (Opinie: ${post.number_of_comments})`;
                item.appendChild(hint);
            }
            item.addEventListener("click", () => {
                selectedPost = post;
                nameInput.value = `${post.name} ${post.surname}`;
                attemptResolve();
            });
            suggestionsDropdown.appendChild(item);
        });

        if (suggestionsDropdown.children.length > 0) {
            clampDropdownToViewport(nameInput, suggestionsDropdown);
            suggestionsDropdown.classList.add("open");
        } else {
            closeSuggestions();
        }
    }

    // Jedyne miejsce, które rozstrzyga, kim jest dodawana osoba - odpalane wyłącznie jawnym
    // kliknięciem (podpowiedzi albo "+ Dodaj nową osobę"), NIGDY samym wpisywaniem/blur, żeby
    // nie tworzyć w tle wpisów, których user jeszcze nie potwierdził. Jeśli wydział nie jest
    // jeszcze wybrany (możliwe tylko w widoku całej uczelni, patrz renderFacultyPicker), zostaje
    // tylko komunikat - klik w tę samą podpowiedź (albo wybór wydziału, patrz onSelect przekazany
    // do renderFacultyPicker wyżej) spróbuje ponownie.
    async function attemptResolve(): Promise<void> {
        if (resolved) return;
        resolveError.textContent = "";
        duplicateNotice.replaceChildren();

        if (selectedPost) {
            // Osoba z podpowiedzi może już mieć REALNY wydział (np. hurtowy import, po prostu
            // jeszcze bez komentarza) - wtedy używamy JEJ wydziału, nie tego z pola niżej (może
            // dotyczyć zupełnie innego wydziału niż aktualnie przeglądany), i nie trzeba go w
            // ogóle przypisywać razem z komentarzem. Dopiero gdy wciąż siedzi w koszyku "Bez
            // przypisanego wydziału" (needs_faculty_confirmation), potrzebny jest wydział z
            // lokalnego pola (patrz facultyPicker wyżej).
            const personName = `${selectedPost.name} ${selectedPost.surname}`;
            if (!selectedPost.needs_faculty_confirmation) {
                closeSuggestions();
                finalizeResolution(selectedPost.id, selectedPost.slug, selectedPost.faculty!, false, false, personName);
                return;
            }
            const facultyId = facultyPicker.getSelection();
            if (!facultyId) {
                resolveError.textContent = "Wybierz najpierw wydział.";
                return;
            }
            closeSuggestions();
            finalizeResolution(selectedPost.id, selectedPost.slug, facultyId, true, false, personName);
            return;
        }

        const facultyId = facultyPicker.getSelection();
        const trimmed = nameInput.value.trim();
        if (!trimmed) return;
        const { name, surname } = splitFullName(trimmed);
        if (name.length < 2 || surname.length < 2) {
            resolveError.textContent = "Podaj imię i nazwisko (co najmniej 2 znaki każde).";
            return;
        }
        if (!facultyId) {
            resolveError.textContent = "Wybierz najpierw wydział.";
            return;
        }

        // Ostatnia szansa złapać duplikat TUŻ przed utworzeniem - poolPosts (użyte w
        // renderSuggestions do "Czy chodziło Ci o:") pomija osoby z jakimkolwiek komentarzem, więc
        // dokładne dopasowanie imienia kogoś, kto już ma opinie, nigdy tam nie trafia. Bez tego
        // klik "+ Dodaj nową osobę" na już istniejącą osobę cicho tworzyłby duplikat profilu
        // (zgłoszenie: brak komunikatu przy dodawaniu osoby, która już jest w bazie).
        const normalizedTrimmed = normalizeForSearch(trimmed);
        const duplicate = allPosts.find(p => normalizeForSearch(`${p.name} ${p.surname}`) === normalizedTrimmed);
        if (duplicate) {
            duplicateNotice.replaceChildren();
            const text = document.createElement("span");
            text.textContent = `„${duplicate.name} ${duplicate.surname}” już jest w naszej bazie - `;
            const link = document.createElement("a");
            link.href = `/${duplicate.slug}`;
            link.target = "_blank";
            link.rel = "noopener";
            link.textContent = "zobacz profil";
            duplicateNotice.append(text, link);
            return;
        }

        closeSuggestions();
        nameInput.disabled = true;
        revealCommentArea();
        commentArea.replaceChildren();
        const creating = document.createElement("p");
        creating.textContent = "Dodawanie...";
        commentArea.appendChild(creating);

        const result = await api.createPost({ name, surname, faculty_id: facultyId });
        if (!result.ok) {
            nameInput.disabled = false;
            resolveError.textContent = `Błąd: ${result.error}`;
            commentArea.replaceChildren();
            return;
        }
        finalizeResolution(result.data.post.id, result.data.post.slug, facultyId, false, true, `${name} ${surname}`);
    }

    function finalizeResolution(
        postId: number, postSlug: string, facultyId: number, needsFixedFaculty: boolean, personCreatedNow: boolean,
        personName: string
    ): void {
        resolved = { postId, facultyId, personCreatedNow };
        nameInput.disabled = true;
        // Ani pole imienia/nazwiska, ani wydziału nie wnoszą już nic nowego, gdy wiadomo, kim jest
        // ta osoba - nazwisko jest już w tytule dialogu (patrz setDialogTitleSuffix niżej), a
        // wydział zrobił swoje (ustalił facultyId). Całkiem znikają (nie tylko disabled) - nie ma
        // już odwrotu do wyboru innej osoby w tym samym dialogu (zgłoszenie: dodane pole ze starą
        // treścią wyglądało na wciąż aktywne/edytowalne, mimo że nie było).
        nameField.hidden = true;
        facultyPicker.element.hidden = true;
        // Podpowiedzi już się nie otworzą ponownie (nameInput jest disabled) - dialog wraca do
        // zwykłego scrollowania, żeby dało się dojechać do przycisku wysyłki w formularzu opinii
        // poniżej, gdy jest wyższy niż ekran (patrz komentarz przy .add-person-dialog wyżej).
        restoreDialogScroll();
        // Tytuł "Dodaj osobę" -> "Dodaj osobę - Imię Nazwisko" - jasny sygnał, że to już
        // KONKRETNA osoba.
        setDialogTitleSuffix(personName);
        // Pasek nad tytułem (patrz facultyContextEl/openAddPersonDialog) - "Dodajesz do
        // wydziału: ..." przestaje być istotne, gdy wiadomo już, kim jest ta osoba (wydział
        // zrobił swoje), więc pasek przełącza się na kontekst KOLEJNEGO kroku - bez osobnego
        // nagłówka nad samym formularzem opinii niżej, żeby nie dublować tej informacji w dwóch
        // miejscach naraz.
        facultyContextEl.textContent = "Dodaj pierwszą opinię";
        revealCommentArea();

        mountCommentForm(postId, postSlug, facultyId, needsFixedFaculty, personCreatedNow);
    }

    async function mountCommentForm(
        postId: number, postSlug: string, facultyId: number, needsFixedFaculty: boolean, personCreatedNow: boolean
    ): Promise<void> {
        commentArea.replaceChildren();
        const loading = document.createElement("p");
        loading.textContent = "Wczytywanie...";
        commentArea.appendChild(loading);

        const subjectsResult = await api.getSubjects(facultyId);
        commentArea.replaceChildren();

        if (!subjectsResult.ok) {
            const error = document.createElement("p");
            error.className = "error";
            error.textContent = `Nie udało się wczytać formularza opinii: ${subjectsResult.error}`;
            commentArea.appendChild(error);
            return;
        }

        // Bez osobnego nagłówka "Dodaj pierwszą opinię" tutaj - tytuł dialogu już mówi, kogo
        // dotyczy (patrz setDialogTitleSuffix w finalizeResolution), a samo pojawienie się pól
        // oceny/przedmiotu/treści jest wystarczająco jednoznaczne, o co chodzi w tym kroku.
        const formContainer = document.createElement("div");
        commentArea.appendChild(formContainer);

        initCommentForm(postId, formContainer, () => {}, {
            subjects: subjectsResult.data.subjects,
            universitySlug: universitySlug ?? undefined,
            facultyCandidates: undefined,
            // Wydział jest już ustalony wcześniej (wyszarzone pole albo wyszukiwarka) - ta osoba
            // wciąż siedzi w koszyku "Bez przypisanego wydziału" (patrz needsFixedFaculty), więc
            // trzeba go przekazać razem z komentarzem (patrz _resolve_faculty w
            // pages/api/comments.py); dla świeżo utworzonego posta wydział jest już realny, nic
            // więcej nie trzeba wysyłać.
            fixedResolvedFacultyId: needsFixedFaculty ? facultyId : undefined,
            onSubmitted: (comment) => {
                // Świeżo utworzona osoba (patrz personCreatedNow) czeka na moderację razem ze
                // swoim pierwszym komentarzem - jej profil jeszcze nie istnieje publicznie (patrz
                // status=Post.STATUS_ACTIVE w views.post_detail), więc przekierowanie tam
                // skończyłoby się 404. Dla NIEJ zostajemy na liście z banerem, tak jak dotychczas.
                if (personCreatedNow) {
                    closeDialog();
                    showAfterCloseNotice(comment, postId);
                    return;
                }
                // Płynne przejście na profil tej osoby zamiast zostawania na liście (tak jak
                // dawniej w quickReview.ts, przed przebudową na ten popup) - już istnieje i jest
                // publicznie widoczna, więc komentarz od razu widać tam (albo widać, że czeka na
                // moderację), zamiast zgadywać z banera nad listą, czy na pewno się zapisał.
                // Parametry w URL (patrz showJustAddedNotice w post_detail.ts) odtwarzają na
                // miejscu ten sam komunikat "Dziękujemy.../czeka na moderację" + widżet "zostaw
                // e-mail", który wcześniej pokazywał się tutaj DOPIERO po zamknięciu dialogu.
                // removeDialogBeforeNavigate() PRZED nawigacją - NIE closeDialog()/dialog.close(),
                // które kolejkuje zdarzenie "close" jako osobne zadanie (fires asynchronicznie) -
                // window.location.href niżej zdążyłby wystartować PRZED faktycznym usunięciem
                // dialogu z DOM, więc pole imienia/nazwiska śledzone jako "niezapisana treść"
                // (patrz trackUnsavedInput/beforeunload w generics.ts) nadal by tam było w
                // momencie sprawdzania - stąd wprost dialog.remove() (synchronicznie), zamiast
                // przez natywne .close() (ten sam problem i to samo rozwiązanie co dawniej w
                // quickReview.ts).
                removeDialogBeforeNavigate();
                const params = new URLSearchParams({ opinia_dodana: comment.status });
                if (comment.status !== "active") params.set("komentarz_id", String(comment.id));
                // Wydział tej osoby jest już znany z tego dialogu (facultyId) - dopisujemy jego
                // slug jako ?wydzial= (patrz profileHref w posts.ts), żeby profil wiedział, że
                // wydział jest już ustalony z kontekstu, i nie pokazywał zbędnego paska z
                // wydziałem (patrz confirmed_faculty w views.post_detail).
                const targetFacultySlug = realFaculties.find(f => f.id === facultyId)?.slug;
                if (targetFacultySlug) params.set("wydzial", targetFacultySlug);
                navigateSmoothly(`/${postSlug}?${params.toString()}`);
            },
        });
    }

    nameInput.addEventListener("input", () => {
        selectedPost = undefined;
        renderSuggestions(nameInput.value);
    });
    nameInput.addEventListener("focus", () => {
        if (!resolved) renderSuggestions(nameInput.value);
    });
    document.addEventListener("click", (event) => {
        if (!nameSearchWrapper.contains(event.target as Node)) closeSuggestions();
    });
    attachDropdownKeyboardNav(nameInput, suggestionsDropdown);

    if (prefillQuery.trim()) renderSuggestions(prefillQuery);
}

// Podziękowanie + status moderacji - TYLKO dla świeżo utworzonej osoby (patrz personCreatedNow w
// onSubmitted wyżej), której profil jeszcze nie istnieje publicznie (czeka na moderację razem z
// pierwszym komentarzem), więc nie da się tam przekierować. Pokazuje się na stronie DOPIERO po
// zamknięciu popupu, jako baner nad treścią, tym samym wzorcem co dawny quick-review-thanks-banner.
function showAfterCloseNotice(comment: Comment, newPostId: number): void {
    const banner = document.createElement("div");
    banner.className = "add-person-thanks-banner";

    const text = document.createElement("p");
    text.textContent = comment.status === "active"
        ? "Dziękujemy za podzielenie się opinią!"
        : "Dziękujemy! Twoja opinia zostanie opublikowana po sprawdzeniu przez moderację.";
    banner.appendChild(text);

    const extra = document.createElement("p");
    extra.textContent = "Nowo dodana osoba też czeka na sprawdzenie przez moderację.";
    banner.appendChild(extra);

    const targets: { model: NotifyModelKey, pk: number }[] = [{ model: "post", pk: newPostId }];
    if (comment.status !== "active") targets.push({ model: "comment", pk: comment.id });
    banner.appendChild(renderPostHocNotifyPrompt(targets, () => banner.remove()));

    document.querySelector("main")?.prepend(banner);
}

// Wyszukiwarka wydziału z możliwością dopisania nowego, gdy nie ma go na liście (analogicznie
// do przedmiotów) - dostępne tylko, gdy przeglądamy konkretną uczelnię (nowy wydział musi do
// czegoś należeć).
function renderFacultyPicker(
    universitySlug: string | null,
    faculties: Faculty[],
    onSelect?: () => void
): { element: HTMLElement, getSelection: () => number | undefined, reset: () => void } {
    const wrapper = document.createElement("label");
    wrapper.className = "lecturer-field";

    const labelText = document.createElement("span");
    labelText.className = "field-label-text";
    labelText.textContent = "Wydział:";

    const searchWrapper = document.createElement("div");
    searchWrapper.className = "search-with-suggestions";

    // Widżet "zostaw e-mail" dla nowo dopisanego wydziału (patrz notifyEmail.ts) - pojawia się
    // DOPIERO po jego zapisaniu w bazie, osobno od e-maila dla samego wykładowcy (ten formularz
    // już nie zbiera go z góry - patrz notifyPromptContainer w renderAddPersonForm).
    const facultyNotifyPromptContainer = document.createElement("div");

    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = "Szukaj wydziału...";
    input.autocomplete = "off";

    const dropdown = document.createElement("div");
    dropdown.className = "search-suggestions";

    let selection: number | undefined = undefined;

    function closeDropdown(): void {
        dropdown.classList.remove("open");
    }

    function selectFaculty(faculty: Faculty): void {
        selection = faculty.id;
        input.value = faculty.name;
        closeDropdown();
        onSelect?.();
    }

    function renderOptions(query: string): void {
        dropdown.replaceChildren();
        const normalizedQuery = normalizeForSearch(query);

        const matches = normalizedQuery
            ? faculties.filter(f => normalizeForSearch(f.name).includes(normalizedQuery))
            : faculties;

        matches.forEach(faculty => {
            const item = document.createElement("button");
            item.type = "button";
            item.className = "search-suggestion-item";
            item.textContent = faculty.name;
            item.addEventListener("click", () => selectFaculty(faculty));
            dropdown.appendChild(item);
        });

        const trimmedQuery = query.trim();
        const exactMatch = faculties.some(f => normalizeForSearch(f.name) === normalizedQuery);
        if (universitySlug && trimmedQuery && !exactMatch) {
            const createItem = document.createElement("button");
            createItem.type = "button";
            createItem.className = "search-suggestion-item search-suggestion-create";
            createItem.textContent = `+ Dodaj wydział: "${trimmedQuery}"`;
            createItem.addEventListener("click", async () => {
                createItem.disabled = true;
                createItem.textContent = "Dodawanie...";
                const res = await api.createFaculty(trimmedQuery, universitySlug);
                if (res.ok) {
                    if (!faculties.some(f => f.id === res.data.id)) {
                        faculties.push(res.data);
                    }
                    selectFaculty(res.data);
                    facultyNotifyPromptContainer.replaceChildren(
                        renderPostHocNotifyPrompt([{ model: "faculty", pk: res.data.id }]),
                    );
                } else {
                    createItem.disabled = false;
                    createItem.textContent = `Błąd: ${res.error}`;
                }
            });
            dropdown.appendChild(createItem);
        }

        if (dropdown.children.length > 0) {
            clampDropdownToViewport(input, dropdown);
            dropdown.classList.add("open");
        } else {
            closeDropdown();
        }
    }

    input.addEventListener("input", () => {
        selection = undefined;
        // NIE chowamy facultyNotifyPromptContainer tutaj - patrz analogiczna notka w
        // renderUniversityOptions w landing2.ts.
        renderOptions(input.value);
    });

    input.addEventListener("focus", () => {
        renderOptions(input.value);
    });

    document.addEventListener("click", (event) => {
        if (!searchWrapper.contains(event.target as Node)) {
            closeDropdown();
        }
    });

    attachDropdownKeyboardNav(input, dropdown);

    searchWrapper.append(input, dropdown);
    wrapper.append(labelText, searchWrapper, facultyNotifyPromptContainer);

    return {
        element: wrapper,
        getSelection: () => selection,
        reset: () => {
            selection = undefined;
            input.value = "";
            closeDropdown();
        },
    };
}
